// Trying not to use a validation library
// import Vue from "vue";
// import VeeValidate from "vee-validate";
// Vue.use(VeeValidate);

import _Vue from "vue";
import messages, { translate, keys } from "@/plugins/i18n"; // <-- notice the changed import
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { LocalDate, LocalTime } from "@js-joda/core";
import { ChronoUnit } from "@js-joda/core";
import Guid from "@/app/types/common/guid";
import { validateBrewmanBarcode } from "@/plugins/bmBarcodeValidator";
import { EnumBrewmanBarcodeType } from "@/app/services/generated/enums/enumBrewmanBarcodeType.generated";

export type ValidationRule<T = any> = (val: T) => boolean | string;

/// Library Functions ///

// https://www.braemoor.co.uk/software/postcodes.shtml
function checkPostCode(toCheck: string): boolean {
  // Permitted letters depend upon their position in the postcode.
  const alpha1 = "[abcdefghijklmnoprstuwyz]"; // Character 1
  const alpha2 = "[abcdefghklmnopqrstuvwxy]"; // Character 2
  const alpha3 = "[abcdefghjkpmnrstuvwxy]"; // Character 3
  const alpha4 = "[abehmnprvwxy]"; // Character 4
  const alpha5 = "[abdefghjlnpqrstuwxyz]"; // Character 5
  const BFPOa5 = "[abdefghjlnpqrst]"; // BFPO alpha5
  const BFPOa6 = "[abdefghjlnpqrstuwzyz]"; // BFPO alpha6

  // Array holds the regular expressions for the valid postcodes
  const pcexp: RegExp[] = [];

  // BFPO postcodes
  pcexp.push(
    new RegExp("^(bf1)(\\s*)([0-6]{1}" + BFPOa5 + "{1}" + BFPOa6 + "{1})$", "i")
  );

  // Expression for postcodes: AN NAA, ANN NAA, AAN NAA, and AANN NAA
  pcexp.push(
    new RegExp(
      "^(" +
        alpha1 +
        "{1}" +
        alpha2 +
        "?[0-9]{1,2})(\\s*)([0-9]{1}" +
        alpha5 +
        "{2})$",
      "i"
    )
  );

  // Expression for postcodes: ANA NAA
  pcexp.push(
    new RegExp(
      "^(" +
        alpha1 +
        "{1}[0-9]{1}" +
        alpha3 +
        "{1})(\\s*)([0-9]{1}" +
        alpha5 +
        "{2})$",
      "i"
    )
  );

  // Expression for postcodes: AANA  NAA
  pcexp.push(
    new RegExp(
      "^(" +
        alpha1 +
        "{1}" +
        alpha2 +
        "{1}" +
        "?[0-9]{1}" +
        alpha4 +
        "{1})(\\s*)([0-9]{1}" +
        alpha5 +
        "{2})$",
      "i"
    )
  );

  // Exception for the special postcode GIR 0AA
  pcexp.push(/^(GIR)(\s*)(0AA)$/i);

  // Standard BFPO numbers
  pcexp.push(/^(bfpo)(\s*)([0-9]{1,4})$/i);

  // c/o BFPO numbers
  pcexp.push(/^(bfpo)(\s*)(c\/o\s*[0-9]{1,3})$/i);

  // Overseas Territories
  pcexp.push(/^([A-Z]{4})(\s*)(1ZZ)$/i);

  // Anguilla
  pcexp.push(/^(ai-2640)$/i);

  // Load up the string to check
  let postCode = toCheck;

  // Assume we're not going to find a valid postcode
  let valid = false;

  // Check the string against the types of post codes
  for (let i = 0; i < pcexp.length; i++) {
    if (pcexp[i].test(postCode)) {
      // The post code is valid - split the post code into component parts
      pcexp[i].exec(postCode);

      // Copy it back into the original string, converting it to uppercase and inserting a space
      // between the inward and outward codes
      postCode = RegExp.$1.toUpperCase() + " " + RegExp.$3.toUpperCase();

      // If it is a BFPO c/o type postcode, tidy up the "c/o" part
      postCode = postCode.replace(/C\/O\s*/, "c/o ");

      // If it is the Anguilla overseas territory postcode, we need to treat it specially
      if (toCheck.toUpperCase() == "AI-2640") {
        postCode = "AI-2640";
      }

      // Load new postcode back into the form element
      valid = true;

      // Remember that we have found that the code is valid and break from loop
      break;
    }
  }

  // Return with either the reformatted valid postcode or the original invalid postcode
  return valid;
}

/**
 * A factory class for constructing validation rules.
 */
export class RuleSet {
  public notNegativeNumber(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == null || v === "") {
        return true; // ignore undefined or null values
      }

      if (isNaN(Number(v))) {
        return translate(keys.validation_not_number).toString();
      }

      if (Number(v) < 0) {
        return translate(keys.validation_negative).toString();
      }

      return true;
    };
  }

  public notPositiveNumber(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == null || v === "") {
        return true; // ignore undefined or null values
      }

      if (isNaN(Number(v))) {
        return translate(keys.validation_not_number).toString();
      }

      if (Number(v) > 0) {
        return translate(keys.validation_positive).toString();
      }

      return true;
    };
  }

  public notZero(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == null || v === "") {
        return true; // ignore undefined or null values
      }

      if (isNaN(Number(v))) {
        return translate(keys.validation_not_number).toString();
      }

      if (Number(v) === 0) {
        return translate(keys.validation_zero).toString();
      }

      return true;
    };
  }

  public numberInRange(
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    minInclusive = true,
    maxInclusive = true,
    validationErrorMessageKey = keys.validation_number_not_in_range,
    messageParameters: object | null = null
  ): ValidationRule {
    return (v: any): boolean | string => {
      if (v == null || v === "") {
        return true; // ignore undefined or null values
      }

      if (isNaN(Number(v))) {
        return translate(keys.validation_not_number).toString();
      }

      if (v !== undefined && (Number(v) <= min || Number(v) >= max)) {
        if (
          !(
            (minInclusive && Number(v) === min) ||
            (maxInclusive && Number(v) === max)
          )
        ) {
          return translate(
            validationErrorMessageKey,
            messageParameters ? messageParameters : { min, max }
          ).toString();
        }
      }
      return true;
    };
  }

  public required(
    showRequiredIndicator = true, // Whether or not the * is next to the field name
    requiredErrorTextKey = keys.validation_required // Supports custom error text, but assume default
  ): ValidationRule {
    let required = (v: any): boolean | string => {
      if (v === null || v === undefined) {
        return translate(requiredErrorTextKey).toString();
      }

      // Required arrays must contain at least one value.
      if (Array.isArray(v) && v.length === 0) {
        return translate(requiredErrorTextKey).toString();
      }

      // Required strings
      if (typeof v === "string" && v.trim().length == 0) {
        return translate(requiredErrorTextKey).toString();
      }

      // Required Guid's
      if (v.toString() === Guid.EMPTY) {
        return translate(requiredErrorTextKey).toString();
      }

      // Required booleans
      if (typeof v === "boolean" && v === false) {
        return translate(requiredErrorTextKey).toString();
      }

      return true;
    };
    Object.assign(required, {
      showRequiredIndicator: showRequiredIndicator,
    });
    return required;
  }

  public textLength(min: number = 0, max: number = 1000): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      const stringVal = String(v);

      if (stringVal.length < min) {
        return translate(keys.validation_text_short).toString();
      }

      if (stringVal.length > max) {
        return translate(keys.validation_text_long).toString();
      }

      return true;
    };
  }

  public internationalUnicodeLettersAndNumeric(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true;
      }

      const stringVal = String(v);
      const regex = /^[0-9\p{L}]*$/u;

      if (!regex.test(stringVal)) {
        return translate(
          keys.validation_invalid_international_alpha_numeric
        ).toString();
      }

      return true;
    };
  }

  public emails(): ValidationRule {
    // Supports validation for semi-colon separated email values
    return (v: any): boolean | string => {
      if (!v) {
        return true; // ignore undefined or null values
      }

      const stringVal = String(v);

      /* eslint-disable no-useless-escape */
      const re =
        /^((([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(\s*;\s*|\s*))+$/;
      /* eslint-enable no-useless-escape */

      if (!re.test(stringVal.toLowerCase())) {
        return translate(keys.validation_invalid_emails).toString();
      }

      return true;
    };
  }

  public email(): ValidationRule {
    return (v: any): boolean | string => {
      if (!v) {
        return true; // ignore undefined or null values
      }

      const stringVal = String(v);

      /* eslint-disable no-useless-escape */
      const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      /* eslint-enable no-useless-escape */

      if (!re.test(stringVal.toLowerCase())) {
        return translate(keys.validation_invalid_email).toString();
      }

      return true;
    };
  }

  public password(passwordToMatch?: string): ValidationRule {
    return (v: any): boolean | string => {
      if (!v) {
        return true; // ignore undefined or null values
      }

      const stringVal = String(v);

      // Regex forces at least 7 + 1 characters to match with the last not being whitespace
      // Matches even when the last character is the required
      // eslint-disable-next-line no-useless-escape
      const re = /^(?!\s)((?=.*[A-Za-z])(?=.*(\d|[^a-zA-Z\d])).{7,}?[^\s])$/;

      if (passwordToMatch !== undefined && passwordToMatch !== v) {
        return translate(keys.users_both_passwords_must_match).toString();
      }

      if (!re.test(stringVal)) {
        return translate(keys.validation_invalid_password).toString();
      }

      return true;
    };
  }

  public dateNotInPast(): ValidationRule {
    return (v: any): boolean | string => {
      if (!v) {
        return true; // ignore undefined or null values
      }

      const strVal = String(v);
      const date = tenantService.parseDate(strVal);
      if (date.isBefore(tenantService.today)) {
        return messages.validation_date_in_past;
      }
      return true;
    };
  }

  dateBeforeOrEqual(
    earliestDate: LocalDate | null,
    dateDescription?: string
  ): ValidationRule {
    return (v: any): boolean | string => {
      const strVal = String(v);
      if (strVal.length === 0 || !earliestDate || !v) {
        return true;
      }

      const date = tenantService.parseDate(strVal);
      if (date.isAfter(earliestDate)) {
        if (!dateDescription) {
          dateDescription = earliestDate.toString();
        }
        return (
          messages.validation_date_after_date_given + " " + dateDescription
        );
      }
      return true;
    };
  }

  dateAfterOrEqual(
    earliestDate: LocalDate | null,
    dateDescription?: string
  ): ValidationRule {
    return (v: any): boolean | string => {
      const strVal = String(v);
      if (strVal.length === 0 || !earliestDate || !v) {
        return true;
      }

      const date = tenantService.parseDate(strVal);
      if (date.isBefore(earliestDate)) {
        if (!dateDescription) {
          dateDescription = earliestDate.toString();
        }
        return (
          messages.validation_date_before_date_given + " " + dateDescription
        );
      }
      return true;
    };
  }

  timeBeforeOrEqual(
    earliestTime: LocalTime | null,
    description?: string
  ): ValidationRule {
    return (v: any): boolean | string => {
      const strVal = String(v);
      if (strVal.length === 0 || !earliestTime || !v) {
        return true;
      }

      const time = tenantService.parseFormattedTime(strVal);
      if (!time) {
        return false;
      } else if (time.isAfter(earliestTime)) {
        if (!description) {
          description = earliestTime.toString();
        }
        return messages.validation_time_after_time_given + " " + description;
      }
      return true;
    };
  }

  timeAfterOrEqual(
    earliestTime: LocalTime | null,
    description?: string
  ): ValidationRule {
    return (v: any): boolean | string => {
      const strVal = String(v);
      if (strVal.length === 0 || !earliestTime || !v) {
        return true;
      }

      const time = tenantService.parseFormattedTime(strVal);
      if (!time) {
        return false;
      } else if (time.isBefore(earliestTime)) {
        if (!description) {
          description = earliestTime.toString();
        }
        return messages.validation_time_before_time_given + " " + description;
      }
      return true;
    };
  }

  maxDateRange(maxDateRangeDays: number): ValidationRule<readonly LocalDate[]> {
    return (v: readonly LocalDate[]): boolean | string => {
      if (v.length !== 2) {
        return true;
      }

      const fromDate = v[0];
      const toDate = v[1];

      if (fromDate.isAfter(toDate)) {
        return messages.validation_date_range_invalid;
      }

      if (fromDate.until(toDate, ChronoUnit.DAYS) >= maxDateRangeDays) {
        return messages.validation_date_range_too_long;
      }

      return true;
    };
  }

  public postCode(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      const stringVal = String(v);

      return (
        checkPostCode(stringVal) ||
        translate(keys.validation_invalid_postcode).toString()
      );
    };
  }

  public isBrewmanBarcodeOfTypes(
    labelTypes: EnumBrewmanBarcodeType[]
  ): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty
      }

      const stringVal = String(v);

      let validBarcodeType = validateBrewmanBarcode(stringVal);

      if (!labelTypes.includes(validBarcodeType)) {
        if (validBarcodeType == EnumBrewmanBarcodeType.none) {
          return translate(keys.validation_invalid_barcode).toString();
        }

        return translate(keys.validation_wrong_barcode).toString();
      }

      return true;
    };
  }

  public notContainedInArray(
    existingValues: readonly any[],
    validationErrorMessage: string = messages.validation_duplicate_value_already_exists
  ): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty strings
      }

      // Check for guids equivalency
      if (Guid.isGuid(v) && existingValues.every((x) => Guid.isGuid(x))) {
        return (
          !existingValues.some((existingValue) => existingValue.equals(v)) ||
          validationErrorMessage
        );
      }

      return (
        !existingValues.some((existingValue) => existingValue === v) ||
        validationErrorMessage
      );
    };
  }

  public containedInArray(
    existingValues: any[],
    validationErrorMessage: string = messages.validation_duplicate_value_already_exists
  ): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty strings
      }

      // Check for guids equivalency
      if (Guid.isGuid(v) && existingValues.every((x) => Guid.isGuid(x))) {
        return (
          existingValues.some((existingValue) => existingValue.equals(v)) ||
          validationErrorMessage
        );
      }

      return (
        existingValues.some((existingValue) => existingValue === v) ||
        validationErrorMessage
      );
    };
  }

  public notUrl(): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty strings
      }

      const stringVal = String(v);

      const urlRegex =
        /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;

      if (urlRegex.test(stringVal)) {
        return translate(keys.validation_no_url).toString();
      }

      return true;
    };
  }

  public cannotContainWords(words: string[]): ValidationRule {
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty strings
      }

      const stringVal = String(v);

      let matchingKeyword: string = "";

      if (
        words.some((word) => {
          if (stringVal.includes(word)) {
            matchingKeyword = word;
            return true;
          }
        })
      ) {
        return translate(keys.validation_cannot_contain_word, {
          word: matchingKeyword,
        }).toString();
      }

      return true;
    };
  }

  public internationalPhoneNumber(): ValidationRule {
    // E.164 format (https://en.wikipedia.org/wiki/E.164)
    return (v: any): boolean | string => {
      if (v == undefined) {
        return true; // ignore undefined or null values
      }

      if (v === "") {
        return true; // ignore empty strings
      }

      const stringVal = String(v);

      const phoneRegex = /^\+[1-9]\d{1,14}$/;

      if (!phoneRegex.test(stringVal)) {
        return translate(keys.validation_invalid_phone_number).toString();
      }

      return true;
    };
  }
}
export const ruleSet = new RuleSet();

export function RuleSetPlugin(Vue: typeof _Vue): void {
  Vue.prototype.$ruleSet = ruleSet;
}

export const useRuleSets = () => ({
  ruleSet,
});

_Vue.use(RuleSetPlugin);
